import LoadSpinner from 'legacyfrontend/components/spinner';
import ErrorSnackbar from 'legacyfrontend/components/ErrorSnackbar';
import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useGlobal } from 'legacyfrontend/contexts/GlobalContext';
import PackagesApi from '../../../api/paquetes';
import Flex from '../../../components/Flex';
import { getCacheStatus } from 'legacyfrontend/helpers/getCacheStatus';
import RefreshModal from 'legacyfrontend/components/home/components/RefreshModal';
import { useSelector } from 'react-redux';
import { CACHE_DEFAULTS, SESSION_STORAGE_KEYS } from 'legacyfrontend/constants';
import { useColors } from 'legacyfrontend/hooks/useColors';

const HotelsListView = () => {
  const { t } = useTranslation('layout');
  const { onSearchCount, registerOnSearch } = useGlobal();
  const { search } = useLocation();
  const [hotels, setHotels] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [openToast, setOpenToast] = useState(false);
  const delegation = useSelector((state) => state.applicationState.delegation);
  const { background, text } = useColors();

  const hasCalledApiRef = useRef(false);

  const searchParamsToObject = useMemo(() => {
    if (!search) return {};

    const params = new URLSearchParams(search);
    const filters = {};

    params.forEach((value, key) => {
      if (key === 'passengers' || key === 'adultsQty' || key === 'childrenQty') {
        filters[key] = value.split(',').map(Number);
      } else {
        filters[key] = value;
      }
    });

    return filters;
  }, [search]);

  const hasValidSearchParams = useMemo(() => {
    if (!search) return false;

    const searchParams = new URLSearchParams(search);
    const hasDestination = searchParams.get('destinationId') && searchParams.get('destinationName');
    const hasCheckIn = searchParams.get('checkInDate');
    const hasCheckOut = searchParams.get('checkOutDate');

    return hasDestination && hasCheckIn && hasCheckOut;
  }, [search]);

  const showErrorMessage = useCallback((message) => {
    setErrorMessage(message);
    setOpenToast(true);
  }, []);

  const handleCloseToast = useCallback(() => {
    setOpenToast(false);
  }, []);

  const handleFetchHotels = useCallback(async () => {
    if (!hasValidSearchParams) {
      setHotels([]);
      setIsLoading(false);
      setError(false);
      return;
    }

    if (isLoading) {
      return;
    }

    setError(false);

    const {
      destinationId,
      destinationName,
      checkInDate,
      checkOutDate,
      adultsQty = 2,
      childrenQty = 0,
    } = searchParamsToObject;

    const cacheValue = getCacheStatus();

    const queryParams = new URLSearchParams({
      destinationId,
      destinationName,
      checkInDate,
      checkOutDate,
      adultsQty: adultsQty || 2,
      childrenQty: childrenQty || 0,
      cache: cacheValue,
      delegation: delegation || '',
    });

    try {
      setIsLoading(true);
      const dataResponse = await PackagesApi.getPackages(`?${queryParams.toString()}`);
      setHotels(dataResponse?.data || []);
      setError(false);
    } catch (error) {
      let errorMessage = 'Error al cargar los hoteles';

      if (error?.response?.status === 500) {
        errorMessage = 'Error del servidor. Por favor, intenta nuevamente en unos momentos.';
      } else if (error?.response?.status === 404) {
        errorMessage = 'No se encontraron hoteles con los criterios especificados.';
      } else if (error?.code === 'ERR_NETWORK') {
        errorMessage = 'Error de conexión. Verifica tu conexión a internet.';
      } else if (error?.message) {
        errorMessage = error.message;
      }

      showErrorMessage(errorMessage);
      setError(true);
      setHotels([]);
    } finally {
      setIsLoading(false);
    }
  }, [hasValidSearchParams, isLoading, searchParamsToObject, delegation, showErrorMessage]);

  const handleRefresh = useCallback(() => {
    handleFetchHotels();
  }, [handleFetchHotels]);

  useEffect(() => {
    if (hasValidSearchParams && !isLoading && !hasCalledApiRef.current) {
      hasCalledApiRef.current = true;
      handleFetchHotels();
    }
  }, [hasValidSearchParams, search, isLoading, handleFetchHotels]);

  if (isLoading) {
    return (
      <div
        className='container flight__list-view__area loading-flights'
        style={{ textAlign: 'center', padding: '4rem 2rem' }}
      >
        <LoadSpinner />
        <h2 style={{ marginTop: '2rem' }}>Buscando los mejores hoteles para ti</h2>
        <p style={{ marginTop: '1rem', color: '#666' }}>Esto puede tomar unos segundos...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className='container flight__list-view__area flight__list-view__area--error'>
        <h2>¡Ups! Algo salió mal</h2>
        <p style={{ textAlign: 'center', padding: '0 2rem' }}>
          No pudimos cargar los hoteles. Por favor, intenta nuevamente en unos momentos o ajuste los
          filtros de búsqueda.
        </p>
        <button
          onClick={handleRefresh}
          style={{
            backgroundColor: background,
            color: text,
            border: `2px solid ${background}`,
            padding: '12px 24px',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: '500',
            cursor: 'pointer',
            marginTop: '20px',
            transition: 'all 0.3s ease',
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = 'transparent';
            e.target.style.color = background;
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = background;
            e.target.style.color = text;
          }}
        >
          🔄 Reintentar búsqueda
        </button>
        <ErrorSnackbar message={errorMessage} onClose={handleCloseToast} open={openToast} />
      </div>
    );
  }

  if (!hotels || hotels.length === 0) {
    return (
      <div className='container flight__list-view__area flight__list-view__area--empty'>
        <h2>No encontramos hoteles disponibles</h2>
        <p style={{ textAlign: 'center', padding: '0 2rem' }}>
          Prueba ajustando los filtros de búsqueda o seleccionando fechas diferentes.
        </p>
      </div>
    );
  }

  return (
    <div className='flight__list-view__area'>
      <Flex className='flight__list__view__wrapper--flight'>
        <Flex className='flight__list__view__wrapper--flight__results' vertical>
          {hotels.map((hotel, index) => {
            if (!hotel) {
              return null;
            }
            return (
              <div key={`hotel-${index}`} style={{ padding: '1rem', marginBottom: '1rem', border: '1px solid #ddd', borderRadius: '8px' }}>
                <h3>{hotel.name || 'Hotel sin nombre'}</h3>
                <p>{hotel.description || 'Sin descripción'}</p>
                {/* TODO: Implementar tarjeta de hotel completa */}
              </div>
            );
          })}
        </Flex>
      </Flex>
      <ErrorSnackbar message={errorMessage} onClose={handleCloseToast} open={openToast} />
    </div>
  );
};

export default HotelsListView;
